import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import showNotification from '../component/Notification';
import baseURL from '../component/baseURL';

/** Turn API labels like StandardStatus → "Standard Status" */
function formatFieldLabel(label) {
    if (!label || typeof label !== 'string') return '';
    if (label.includes('/') || label.includes('(')) return label;
    return label
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .trim();
}

function normalizeAddressList(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') return Object.values(payload);
    return [];
}

function formatPropertyTypeDisplay(typeStr) {
    const s = String(typeStr ?? '').trim();
    if (!s) return '';
    return s
        .split(' ')
        .map((w) => {
            if (w === '/') return '/';
            const lw = w.toLowerCase();
            return lw.charAt(0).toUpperCase() + lw.slice(1);
        })
        .join(' ');
}

/** Max file size per image (bytes); reject when strictly larger than this (15 MB). */
const MAX_LISTING_IMAGE_BYTES = 15 * 1024 * 1024;
const MAX_LISTING_IMAGE_COUNT = 10;

const ALLOWED_IMAGE_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']);

function isAllowedListingImageFile(file) {
    const type = (file.type || '').toLowerCase();
    if (ALLOWED_IMAGE_MIME.has(type)) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp)$/i.test(name);
}

/** Internal drag type so file drop on the dropzone does not conflict */
const REORDER_DRAG_TYPE = 'application/x-edit-listing-reorder';

function moveItemInArray(arr, fromIndex, toIndex) {
    if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= arr.length ||
        toIndex >= arr.length
    ) {
        return [...arr];
    }
    const next = [...arr];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    return next;
}

const STANDARD_STATUS_OPTIONS = [
    'Active',
    'Coming Soon',
    'Sold',
    'Nearly Sold',
    'Reserve',
    'Under Offer',
    'Inactive',
];

/** Value from `before_edit_listing_fetch_fields` listing_field_ids[].data */
function valueFromListingFieldData(f) {
    const d = f?.data;
    if (!d || typeof d !== 'object') return '';
    const key = f.field_name;
    if (Object.prototype.hasOwnProperty.call(d, key) && d[key] != null && d[key] !== '') {
        return String(d[key]);
    }
    const vals = Object.values(d);
    if (vals.length && vals[0] != null && vals[0] !== '') return String(vals[0]);
    return '';
}

/**
 * Edit listing route. Open from PortfolioDetails:
 * navigate('/home/edit-listing', { state: { listingID } })
 *
 * Dynamic fields + listing_privacy / feed_type / images from `before_edit_listing_fetch_fields`.
 */
export default function EditListing() {
    const navigate = useNavigate();
    const location = useLocation();
    const listingID = location.state?.listingID || location.state?.listing_id || '';

    function handleEditListingBack() {
        const returnTo = location.state?.returnTo;
        if (returnTo?.path) {
            navigate(returnTo.path, { replace: true, state: returnTo.state ?? {} });
            return;
        }
        navigate('/home/my-listings', { replace: true, state: { fromSidebar: true } });
    }

    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [listingFieldIds, setListingFieldIds] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [portfolioImages, setPortfolioImages] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [listingType, setListingType] = useState('public');
    const [feedGroup, setFeedGroup] = useState('1');
    const [fieldsReady, setFieldsReady] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    /** While a server image delete is in progress (disable that × only) */
    const [removingImageId, setRemovingImageId] = useState(null);

    const imageInputRef = useRef(null);
    /** Mobile: opens device camera via capture attribute */
    const cameraInputRef = useRef(null);
    /** New files to upload when listing has no pictures yet (same pattern as AddListing) */
    const [listingImages, setListingImages] = useState([]);
    const [imageDropActive, setImageDropActive] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    /** Mobile: show Gallery / Camera only after user taps "Add Image" */
    const [mobileImagePickerOpen, setMobileImagePickerOpen] = useState(false);
    /** Remount camera input so iOS/Android reliably fire change on each new photo */
    const [cameraInputKey, setCameraInputKey] = useState(0);
    const listingImagesRef = useRef(listingImages);
    listingImagesRef.current = listingImages;

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [addressCountryCode, setAddressCountryCode] = useState('');
    const [addressStateId, setAddressStateId] = useState('');
    const [addressCityId, setAddressCityId] = useState('');

    const setField = useCallback((fieldName, value) => {
        setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    }, []);

    const fetchCountryAPI = useCallback(() => {
        fetch(baseURL.URL + '/address/country', { method: 'POST' })
            .then((response) => response.json())
            .then((responseJson) => {
                setCountryList(responseJson.response?.countries || []);
            })
            .catch(() => setCountryList([]));
    }, []);

    const fetchStateAPI = useCallback((countryCode) => {
        if (!countryCode) {
            setStateList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append('country_code', countryCode);
        fetch(baseURL.URL + '/address/state', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const statesPayload = responseJson.response?.states ?? responseJson.response?.regions ?? responseJson.response;
                setStateList(normalizeAddressList(statesPayload));
            })
            .catch(() => setStateList([]));
    }, []);

    const fetchCityAPI = useCallback((stateId, countryCode) => {
        if (!stateId || !countryCode) {
            setCityList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append('country_code', countryCode);
        formdata.append('state_id', stateId);
        fetch(baseURL.URL + '/address/city', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const citiesPayload = responseJson.response?.cities ?? responseJson.response;
                setCityList(normalizeAddressList(citiesPayload));
            })
            .catch(() => setCityList([]));
    }, []);

    useEffect(() => {
        if (!listingID) {
            showNotification('Error', 'No listing selected for edit.', 'danger');
            navigate('/home/my-listings', { replace: true });
        }
    }, [listingID, navigate]);

    useEffect(() => {
        fetchCountryAPI();
    }, [fetchCountryAPI]);

    useEffect(() => {
        if (listingType === 'pocket') setFeedGroup('6');
    }, [listingType]);

    useEffect(() => {
        if (isDataLoading) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isDataLoading]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        return () => {
            listingImagesRef.current.forEach((item) => URL.revokeObjectURL(item.url));
        };
    }, []);

    const ingestListingImages = useCallback((picked) => {
        const files = Array.from(picked || []).filter((f) => f && f.size !== undefined);
        if (!files.length) return;

        setListingImages((prev) => {
            const existingOnServer = portfolioImages.length;
            const room = MAX_LISTING_IMAGE_COUNT - existingOnServer - prev.length;
            if (room <= 0) {
                showNotification(
                    'Error',
                    `You can upload at most ${MAX_LISTING_IMAGE_COUNT} images total (including current pictures).`,
                    'danger'
                );
                return prev;
            }

            const tooLarge = [];
            const badType = [];
            const ok = [];
            files.forEach((file) => {
                if (!isAllowedListingImageFile(file)) {
                    badType.push(file.name || 'file');
                    return;
                }
                if (file.size > MAX_LISTING_IMAGE_BYTES) {
                    tooLarge.push(file.name || 'Image');
                    return;
                }
                ok.push(file);
            });

            if (badType.length) {
                const sample = badType.slice(0, 3).join(', ');
                showNotification(
                    'Error',
                    badType.length === 1
                        ? `"${badType[0]}" is not a supported format. Use JPG, PNG, GIF, or WebP.`
                        : `Some files are not supported (${sample}${badType.length > 3 ? '…' : ''}). Use JPG, PNG, GIF, or WebP.`,
                    'danger'
                );
            }
            if (tooLarge.length) {
                showNotification(
                    'Error',
                    tooLarge.length === 1
                        ? `"${tooLarge[0]}" is larger than 15 MB. Each image must be 15 MB or smaller.`
                        : `${tooLarge.length} file(s) exceed 15 MB: ${tooLarge.join(', ')}. Each image must be 15 MB or smaller.`,
                    'danger'
                );
            }

            const toAdd = ok.slice(0, room);
            if (ok.length > room) {
                showNotification(
                    'Error',
                    `Only ${room} more image slot(s) available (max ${MAX_LISTING_IMAGE_COUNT} total).`,
                    'danger'
                );
            }
            if (!toAdd.length) return prev;

            const newEntries = toAdd.map((file) => ({
                file,
                url: URL.createObjectURL(file),
            }));
            return [...prev, ...newEntries];
        });
    }, [portfolioImages.length]);

    const handleAddImageClick = () => imageInputRef.current?.click();

    const handleMobileCameraClick = () => {
        cameraInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const input = e.target;
        const picked = Array.from(input.files || []);
        input.value = '';
        ingestListingImages(picked);
    };

    /** Camera capture: ingest synchronously, then remount input so the next shot works on iOS/Android */
    const handleCameraImageChange = (e) => {
        const input = e.target;
        const files = input.files;
        if (!files?.length) {
            return;
        }
        const picked = Array.from(files);
        ingestListingImages(picked);
        input.value = '';
        setTimeout(() => setCameraInputKey((k) => k + 1), 150);
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDropActive(false);
        if (e.dataTransfer?.types?.includes?.(REORDER_DRAG_TYPE)) {
            return;
        }
        const files = e.dataTransfer?.files;
        if (!files?.length) return;
        ingestListingImages(files);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDropActive(true);
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) setImageDropActive(false);
    };

    useEffect(() => {
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items?.length) return;
            const files = [];
            for (let i = 0; i < items.length; i += 1) {
                const it = items[i];
                if (it.kind === 'file') {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                }
            }
            if (!files.length) return;
            e.preventDefault();
            ingestListingImages(files);
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [ingestListingImages]);

    const removeListingImage = useCallback((url) => {
        setListingImages((prev) => {
            const removed = prev.find((item) => item.url === url);
            if (removed) URL.revokeObjectURL(removed.url);
            return prev.filter((item) => item.url !== url);
        });
    }, []);

    const fetchFieldsBeforeEditListing = useCallback(
        (userid, listingId) => {
            if (!userid || !listingId) return;
            setFieldsLoading(true);
            setFieldsReady(false);

            const formdata = new FormData();
            formdata.append('user_id', userid);
            formdata.append('listing_id', String(listingId));
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');

            fetch(baseURL.URL + '/portfolio/before_edit_listing_fetch_fields', {
                method: 'POST',
                body: formdata,
            })
                .then((response) => response.json())
                .then((responseJson) => {
                    const code = responseJson?.server?.code ?? responseJson?.server?.result_code;
                    if (Number(code) !== 200) {
                        showNotification('Error', responseJson?.server?.message || 'Could not load edit fields', 'danger');
                        return;
                    }
                    const res = responseJson.response || {};
                    const property_type = res.property_type;
                    const portfolioImg = res.portfolio_images;
                    const containers = res.listing_container_array1;
                    const first = Array.isArray(containers) ? containers[0] : null;
                    const fieldIds = first?.listing_field_ids;
                    const fields = Array.isArray(fieldIds) ? fieldIds : [];

                    const ptList = Array.isArray(property_type) ? property_type : [];
                    setPropertyTypes(ptList);
                    setPortfolioImages(
                        Array.isArray(portfolioImg) ? portfolioImg : portfolioImg ? [portfolioImg] : []
                    );
                    setListingFieldIds(fields);

                    const feed_type = String(res.feed_type ?? '1');
                    const listing_privacy = String(res.listing_privacy ?? '1');
                    setFeedGroup(feed_type);
                    if (feed_type === '6') {
                        setListingType('pocket');
                    } else {
                        setListingType(listing_privacy === '1' ? 'public' : 'pocket');
                    }

                    const initial = {};
                    fields.forEach((f) => {
                        if (!f?.field_name) return;
                        initial[f.field_name] = valueFromListingFieldData(f);
                    });

                    const ptRaw = initial.PropertyType;
                    if (ptRaw && ptList.length) {
                        const matched = ptList.find(
                            (pt) =>
                                String(pt.id) === String(ptRaw) ||
                                formatPropertyTypeDisplay(pt.type).toLowerCase() === String(ptRaw).trim().toLowerCase() ||
                                String(pt.type).toLowerCase() === String(ptRaw).trim().toLowerCase()
                        );
                        if (matched) initial.PropertyType = String(matched.id);
                    }

                    setFieldValues(initial);

                    if (initial.Country) {
                        setAddressCountryCode(String(initial.Country));
                        fetchStateAPI(String(initial.Country));
                    }
                    if (initial.StateOrProvince && initial.Country) {
                        setAddressStateId(String(initial.StateOrProvince));
                        fetchCityAPI(String(initial.StateOrProvince), String(initial.Country));
                    }
                    if (initial.City) {
                        setAddressCityId(String(initial.City));
                    }
                    setFieldsReady(true);
                })
                .catch((error) => {
                    console.error(error);
                    showNotification('Error', 'Could not load listing fields.', 'danger');
                })
                .finally(() => {
                    setFieldsLoading(false);
                });
        },
        [fetchStateAPI, fetchCityAPI]
    );

    useEffect(() => {
        if (!listingID) return;
        const userid = localStorage.getItem('user_id');
        if (!userid) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        fetchFieldsBeforeEditListing(userid, listingID);
    }, [listingID, fetchFieldsBeforeEditListing]);

    const renderDynamicFields = () => {
        const fields = listingFieldIds;
        const out = [];
        let i = 0;
        const hiddenFields = new Set(['Basement', 'Appliances', 'ArchitecturalStyle']);

        while (i < fields.length) {
            const f = fields[i];
            const name = f.field_name;
            const required = Number(f.validationRequired) === 1;

            if (hiddenFields.has(name)) {
                i += 1;
                continue;
            }

            if (name === 'ListingId') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <input
                            type="text"
                            className="add-listing-input"
                            readOnly
                            disabled
                            value={fieldValues.ListingId ?? ''}
                        />
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'LivingArea') {
                const next = fields[i + 1];
                if (next?.field_name === 'LivingAreaUnits') {
                    const label = formatFieldLabel(f.field_name_label || 'BuildArea');
                    out.push(
                        <div key={`pair-living-${i}`} className="add-listing-field-block">
                            <span className="add-listing-field-label">
                                {label}
                                {required && <span className="add-listing-required-star">*</span>}
                            </span>
                            <div className="add-listing-inline-row">
                                <input
                                    type="text"
                                    className="add-listing-input add-listing-input--grow"
                                    placeholder={formatFieldLabel(f.field_name_label || 'LivingArea')}
                                    value={fieldValues.LivingArea ?? ''}
                                    onChange={(e) => setField('LivingArea', e.target.value)}
                                    required={required}
                                />
                                <select
                                    className="add-listing-input add-listing-unit-select"
                                    value={fieldValues.LivingAreaUnits ?? ''}
                                    onChange={(e) => setField('LivingAreaUnits', e.target.value)}
                                    aria-label={formatFieldLabel(next.field_name_label || 'Unit')}
                                    required={Number(next.validationRequired) === 1}
                                >
                                    <option value="">ft² / m²</option>
                                    <option value="Sq Feet">ft²</option>
                                    <option value="Sq Meter">m²</option>
                                </select>
                            </div>
                        </div>
                    );
                    i += 2;
                    continue;
                }
            }

            if (name === 'LotSizeArea') {
                const next = fields[i + 1];
                if (next?.field_name === 'LotSizeUnits') {
                    const label = formatFieldLabel(f.field_name_label || 'LotSizeArea');
                    out.push(
                        <div key={`pair-lot-${i}`} className="add-listing-field-block">
                            <span className="add-listing-field-label">
                                {label}
                                {required && <span className="add-listing-required-star">*</span>}
                            </span>
                            <div className="add-listing-inline-row">
                                <input
                                    type="text"
                                    className="add-listing-input add-listing-input--grow"
                                    placeholder={formatFieldLabel(f.field_name_label || 'LotSizeArea')}
                                    value={fieldValues.LotSizeArea ?? ''}
                                    onChange={(e) => setField('LotSizeArea', e.target.value)}
                                    required={required}
                                />
                                <select
                                    className="add-listing-input add-listing-unit-select"
                                    value={fieldValues.LotSizeUnits ?? ''}
                                    onChange={(e) => setField('LotSizeUnits', e.target.value)}
                                    aria-label={formatFieldLabel(next.field_name_label || 'Unit')}
                                    required={Number(next.validationRequired) === 1}
                                >
                                    <option value="">ft² / m²</option>
                                    <option value="Sq Feet">ft²</option>
                                    <option value="Sq Meter">m²</option>
                                </select>
                            </div>
                        </div>
                    );
                    i += 2;
                    continue;
                }
            }

            if (name === 'LivingAreaUnits' || name === 'LotSizeUnits') {
                i += 1;
                continue;
            }

            if (name === 'Country') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressCountryCode}
                            onChange={(e) => {
                                const code = e.target.value;
                                setAddressCountryCode(code);
                                const item = countryList.find((c) => String(c.code ?? c.id ?? '') === code);
                                const countryName = item ? String(item.name ?? item.country_name ?? '').trim() : '';
                                setField('Country', code);
                                setField('country_name', countryName);
                                setAddressStateId('');
                                setAddressCityId('');
                                setField('StateOrProvince', '');
                                setField('state_name', '');
                                setField('City', '');
                                setField('city_name', '');
                                setStateList([]);
                                setCityList([]);
                                if (code) fetchStateAPI(code);
                            }}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {countryList.map((c) => (
                                <option key={String(c.code ?? c.id ?? c.name)} value={String(c.code ?? c.id ?? '')}>
                                    {c.name ?? c.country_name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'StateOrProvince') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressStateId}
                            onChange={(e) => {
                                const sid = e.target.value;
                                setAddressStateId(sid);
                                const item = stateList.find(
                                    (s) => String(s.region_code ?? s.state_id ?? s.id ?? '') === sid
                                );
                                const stateName = item
                                    ? String(item.region_name ?? item.state_name ?? item.name ?? '').trim()
                                    : '';
                                setField('StateOrProvince', sid);
                                setField('state_name', stateName);
                                setAddressCityId('');
                                setField('City', '');
                                setField('city_name', '');
                                setCityList([]);
                                if (sid && addressCountryCode) fetchCityAPI(sid, addressCountryCode);
                            }}
                            disabled={!addressCountryCode}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {stateList.map((s) => (
                                <option
                                    key={String(s.region_code ?? s.state_id ?? s.id ?? s.region_name)}
                                    value={String(s.region_code ?? s.state_id ?? s.id ?? '')}
                                >
                                    {s.region_name ?? s.state_name ?? s.name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'City') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressCityId}
                            onChange={(e) => {
                                const cid = e.target.value;
                                setAddressCityId(cid);
                                const item = cityList.find((c) => String(c.city_id ?? c.id ?? '') === cid);
                                const cityName = item ? String(item.city_name ?? item.name ?? '').trim() : '';
                                setField('City', cid);
                                setField('city_name', cityName);
                            }}
                            disabled={!addressStateId}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {cityList.map((c) => (
                                <option key={String(c.city_id ?? c.id ?? c.city_name)} value={String(c.city_id ?? c.id ?? '')}>
                                    {c.city_name ?? c.name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'StandardStatus') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={fieldValues.StandardStatus ?? ''}
                            onChange={(e) => setField('StandardStatus', e.target.value)}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {STANDARD_STATUS_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'PropertyType') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={fieldValues.PropertyType ?? ''}
                            onChange={(e) => setField('PropertyType', e.target.value)}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {propertyTypes.map((pt) => (
                                <option key={pt.id} value={String(pt.id)}>
                                    {formatPropertyTypeDisplay(pt.type)}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'PublicRemarks') {
                const label = formatFieldLabel(f.field_name_label || 'PublicRemarks');
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block public-remarks-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <textarea
                            className="add-listing-textarea"
                            placeholder={label}
                            rows={4}
                            value={fieldValues.PublicRemarks ?? ''}
                            onChange={(e) => setField('PublicRemarks', e.target.value)}
                            required={required}
                        />
                    </div>
                );
                i += 1;
                continue;
            }

            const label = formatFieldLabel(f.field_name_label || name);
            out.push(
                <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                    <span className="add-listing-field-label">
                        {label}
                        {required && <span className="add-listing-required-star">*</span>}
                    </span>
                    <input
                        type="text"
                        className="add-listing-input"
                        placeholder={label}
                        value={fieldValues[name] ?? ''}
                        onChange={(e) => setField(name, e.target.value)}
                        required={required}
                    />
                </div>
            );
            i += 1;
        }

        return out;
    };

    async function removeImageAPI(imageId) {
        const id = imageId != null ? String(imageId).trim() : '';
        if (!id || !listingID) return;
        const userID = localStorage.getItem('user_id');
        if (!userID) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        try {
            setRemovingImageId(id);
            const formdata = new FormData();
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
            formdata.append('user_id', userID);
            formdata.append('listing_id', String(listingID));
            formdata.append('image_id', id);
            const response = await fetch(baseURL.URL + '/portfolio/remove_image', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();
            const code = responseJson?.server?.result_code ?? responseJson?.server?.code;
            if (Number(code) === 200) {
                setPortfolioImages((prev) =>
                    prev.filter((img) => String(img.image_id ?? img.imageId ?? '') !== id)
                );
                const serverMsg = responseJson?.server?.message?.trim();
                showNotification(
                    'Success',
                    serverMsg || 'Image removed successfully.',
                    'success'
                );
            } else {
                showNotification('Error', responseJson?.server?.message || 'Could not remove image.', 'danger');
            }
        } catch (error) {
            console.error(error);
            showNotification('Error', 'Could not remove image.', 'danger');
        } finally {
            setRemovingImageId(null);
        }
    }

    function readReorderPayload(e) {
        const raw =
            e.dataTransfer?.getData(REORDER_DRAG_TYPE) || e.dataTransfer?.getData('text/plain') || '';
        if (!raw) return null;
        try {
            const data = JSON.parse(raw);
            if (data && (data.kind === 'server' || data.kind === 'new') && typeof data.index === 'number') {
                return data;
            }
        } catch {
            return null;
        }
        return null;
    }

    const handleReorderDragStart = (e, kind, index) => {
        if (e.target.closest('button')) {
            e.preventDefault();
            return;
        }
        const payload = JSON.stringify({ kind, index });
        try {
            e.dataTransfer.setData(REORDER_DRAG_TYPE, payload);
            e.dataTransfer.setData('text/plain', payload);
        } catch {
            e.dataTransfer.setData('text/plain', payload);
        }
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleReorderDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    async function pictureRearrangeApi(imageIdsOrdered) {
        const userID = localStorage.getItem('user_id');
        if (!userID || !listingID) {
            throw new Error('Missing session or listing.');
        }
        const formdata = new FormData();
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        formdata.append('user_id', userID);
        formdata.append('listing_id', String(listingID));
        formdata.append('image_order', JSON.stringify(imageIdsOrdered));

        const response = await fetch(baseURL.URL + '/portfolio/rearrange_image', {
            method: 'POST',
            body: formdata,
        });
        const responseJson = await response.json();
        const code = responseJson?.server?.result_code ?? responseJson?.server?.code;
        if (Number(code) !== 200) {
            throw new Error(responseJson?.server?.message || 'Could not rearrange images.');
        }
        const rearrangeMsg = responseJson?.server?.message?.trim();
        showNotification(
            'Success',
            rearrangeMsg || 'Image rearrange successful.',
            'success'
        );
    }

    const handleReorderDropServer = async (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const data = readReorderPayload(e);
        if (!data || data.kind !== 'server') return;
        const fromIndex = data.index;
        if (fromIndex === dropIndex) return;

        const prev = [...portfolioImages];
        const newOrder = moveItemInArray(prev, fromIndex, dropIndex);
        setPortfolioImages(newOrder);
        const ids = newOrder.map((img) => String(img.image_id ?? img.imageId)).filter(Boolean);
        try {
            await pictureRearrangeApi(ids);
        } catch (err) {
            console.error(err);
            setPortfolioImages(prev);
            showNotification('Error', err?.message || 'Could not rearrange images.', 'danger');
        }
    };

    const handleReorderDropNew = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const data = readReorderPayload(e);
        if (!data || data.kind !== 'new') return;
        const fromIndex = data.index;
        if (fromIndex === dropIndex) return;
        setListingImages((prev) => moveItemInArray([...prev], fromIndex, dropIndex));
    };

    /**
     * POST /portfolio/attach_image_files for each new local file (`listingImages`).
     * Uses `listingID` from route state.
     */
    async function updateImageAPI(userId, listingId) {
        if (!userId || !listingId) {
            throw new Error('Missing user or listing id');
        }
        if (!listingImages.length) {
            return;
        }

        const oversized = listingImages.map((x) => x.file).filter((f) => f.size > MAX_LISTING_IMAGE_BYTES);
        if (oversized.length) {
            showNotification(
                'Error',
                'One or more images exceed 15 MB. Remove them and add smaller files.',
                'danger'
            );
            throw new Error('oversized');
        }

        const toBase64DataUrl = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

        const listingIdStr = String(listingId);
        for (let i = 0; i < listingImages.length; i += 1) {
            const b64 = await toBase64DataUrl(listingImages[i].file);
            const formdata = new FormData();
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
            formdata.append('user_id', userId);
            formdata.append('listing_id', listingIdStr);
            formdata.append('album', b64);
            const response = await fetch(baseURL.URL + '/portfolio/attach_image_files', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();
            const uploadCode = responseJson?.server?.result_code ?? responseJson?.server?.code;
            if (Number(uploadCode) !== 200) {
                throw new Error(responseJson?.server?.message || 'Failed to upload image');
            }
        }
    }

    async function updateListingApi() {
        const userID = localStorage.getItem('user_id');
        if (!userID) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        if (!listingID) {
            showNotification('Error', 'Missing listing.', 'danger');
            return;
        }

        const hiddenFields = new Set(['Basement', 'Appliances', 'ArchitecturalStyle']);
        const missingRequired = listingFieldIds.find((f) => {
            if (!f?.field_name || Number(f.validationRequired) !== 1) return false;
            if (hiddenFields.has(f.field_name)) return false;
            const value = fieldValues[f.field_name];
            return value === undefined || value === null || String(value).trim() === '';
        });

        if (missingRequired) {
            showNotification(
                'Error',
                `Please fill ${formatFieldLabel(missingRequired.field_name_label || missingRequired.field_name)}`,
                'danger'
            );
            return;
        }

        try {
            setIsDataLoading(true);
            const payloadFieldValues = {
                ...fieldValues,
            };
            if (payloadFieldValues.PropertyType) {
                const matched = propertyTypes.find(
                    (pt) => String(pt.id) === String(payloadFieldValues.PropertyType)
                );
                payloadFieldValues.PropertyType = formatPropertyTypeDisplay(
                    matched?.type ?? payloadFieldValues.PropertyType
                );
            }

            const formdata = new FormData();
            formdata.append('user_id', userID);
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
            formdata.append('listing_id', String(listingID));
            formdata.append('listing_privacy', listingType === 'public' ? '1' : '0');
            formdata.append('feed_type', String(feedGroup || '1'));
            formdata.append('field', JSON.stringify(payloadFieldValues));

            const response = await fetch(baseURL.URL + '/portfolio/listing_edit', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();
            const code = responseJson?.server?.result_code ?? responseJson?.server?.code;
            const message = responseJson?.server?.message || 'Update failed.';

            if (Number(code) === 200) {
                showNotification(
                    'Success',
                    message || 'Listing updated successfully.',
                    'success'
                );

                if (listingImages.length > 0) {
                    await updateImageAPI(userID, listingID);
                    showNotification('Success', 'Images uploaded successfully.', 'success');
                }

                handleEditListingBack();
                return;
            }

            showNotification('Error', message, 'danger');
        } catch (error) {
            console.error(error);
            if (error?.message === 'oversized') {
                return;
            }
            showNotification('Error', error?.message || 'Could not update listing.', 'danger');
        } finally {
            setIsDataLoading(false);
        }
    }

    if (!listingID) {
        return null;
    }

    return (
        <div className="content-right add-listing-page edit-listing-page">
            {isDataLoading && (
                <div
                    className="edit-listing-fullpage-loader"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    aria-label="Saving listing"
                >
                    <div className="react-js-loader-spinner" />
                    <p className="react-js-loader-text">Saving…</p>
                </div>
            )}
            <button type="button" className="add-listing-desktop-back" onClick={handleEditListingBack}>
                ← Back
            </button>
            <h2 className="add-listing-page-title" style={{ marginBottom: '16px', fontSize: '1.25rem', fontWeight: 700 }}>
                Edit listing
            </h2>

            {fieldsLoading && (
                <div className="add-listing-loading">
                    <p className="add-listing-loading-text">Loading listing fields…</p>
                </div>
            )}

            {!fieldsLoading && fieldsReady && (
                <>
                    <div className="add-listing-form">
                        <div className="add-listing-field-block">
                            <span className="add-listing-field-label">Listing Type</span>
                            <div className="add-listing-segment add-listing-segment--two" role="group" aria-label="Listing type">
                                <button
                                    type="button"
                                    className={listingType === 'public' ? 'active' : ''}
                                    onClick={() => {
                                        setListingType('public');
                                        if (feedGroup === '6') setFeedGroup('1');
                                    }}
                                >
                                    Public
                                </button>
                                <button
                                    type="button"
                                    className={listingType === 'pocket' ? 'active' : ''}
                                    onClick={() => setListingType('pocket')}
                                >
                                    Pocket
                                </button>
                            </div>
                        </div>

                        <div className="add-listing-field-block">
                            <span className="add-listing-field-label">Portfolio Feed Group</span>
                            {listingType === 'pocket' ? (
                                <div className="add-listing-segment add-listing-segment--five" role="group" aria-label="Portfolio feed group">
                                    <button type="button" className="active" onClick={() => setFeedGroup('6')}>
                                        Pocket Feed
                                    </button>
                                </div>
                            ) : (
                                <div className="add-listing-segment add-listing-segment--five" role="group" aria-label="Portfolio feed group">
                                    {['1', '2', '3', '4', '5'].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            className={feedGroup === n ? 'active' : ''}
                                            onClick={() => setFeedGroup(n)}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="add-listing-field-block">
                            <span className="add-listing-field-label">Add pictures</span>
                            {portfolioImages.length >= MAX_LISTING_IMAGE_COUNT ? (
                                <p className="add-listing-loading-text" style={{ marginTop: 8 }}>
                                    Maximum {MAX_LISTING_IMAGE_COUNT} images. Remove a thumbnail below to add more.
                                </p>
                            ) : (
                                <>
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                                        multiple
                                        className="add-listing-file-input"
                                        onChange={handleImageChange}
                                    />
                                    {isMobile && (
                                        <input
                                            key={cameraInputKey}
                                            ref={cameraInputRef}
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            className="add-listing-file-input"
                                            onChange={handleCameraImageChange}
                                        />
                                    )}
                                    {isMobile ? (
                                        <div className="add-listing-mobile-image-pick" role="group" aria-label="Add pictures">
                                            {!mobileImagePickerOpen ? (
                                                <button
                                                    type="button"
                                                    className="add-listing-mobile-add-image-btn"
                                                    onClick={() => setMobileImagePickerOpen(true)}
                                                    disabled={isDataLoading}
                                                >
                                                    Add Image
                                                </button>
                                            ) : (
                                                <>
                                                    <div className="add-listing-segment add-listing-segment--two add-listing-segment--image-source">
                                                        <button
                                                            type="button"
                                                            onClick={handleAddImageClick}
                                                            disabled={isDataLoading}
                                                            aria-label="Choose from gallery"
                                                        >
                                                            Gallery
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleMobileCameraClick}
                                                            disabled={isDataLoading}
                                                            aria-label="Take photo with camera"
                                                        >
                                                            Camera
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="add-listing-mobile-image-cancel"
                                                        onClick={() => setMobileImagePickerOpen(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <p className="add-listing-mobile-image-hint">
                                                        Gallery or Camera (max {MAX_LISTING_IMAGE_COUNT} images total including
                                                        current pictures, 15MB each).
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            className={`add-listing-dropzone${imageDropActive ? ' add-listing-dropzone--active' : ''}`}
                                            onClick={handleAddImageClick}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleAddImageClick();
                                                }
                                            }}
                                            onDragEnter={handleImageDragOver}
                                            onDragOver={handleImageDragOver}
                                            onDragLeave={handleImageDragLeave}
                                            onDrop={handleImageDrop}
                                        >
                                            <span className="add-listing-dropzone-icon" aria-hidden>
                                                +
                                            </span>
                                            <span className="add-listing-dropzone-title">Click to upload multiple images</span>
                                            <span className="add-listing-dropzone-sub">or drag and drop images here</span>
                                            <span className="add-listing-dropzone-meta">
                                                Supports: JPG, PNG, GIF, WebP (max {MAX_LISTING_IMAGE_COUNT} total, 15MB each)
                                            </span>
                                            <span className="add-listing-dropzone-paste">
                                                Press Ctrl+V to paste images from clipboard
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                            {(portfolioImages.length > 0 || listingImages.length > 0) && (
                                <div className="add-listing-image-previews edit-listing-image-previews" style={{ marginTop: 12 }}>
                                    {portfolioImages.map((img, idx) => {
                                        const imgKey = `srv-${img.image_id || img.imageId || img.imagename}`;
                                        const rid = img.image_id ?? img.imageId;
                                        return (
                                            <div
                                                key={imgKey}
                                                className="add-listing-thumb-wrap edit-listing-thumb-wrap--draggable"
                                                draggable
                                                title="Drag to reorder"
                                                onDragStart={(e) => handleReorderDragStart(e, 'server', idx)}
                                                onDragOver={handleReorderDragOver}
                                                onDrop={(e) => {
                                                    void handleReorderDropServer(e, idx);
                                                }}
                                            >
                                                <img src={img.imagename || img.image_url || img.url} alt="" className="add-listing-thumb" draggable={false} />
                                                {rid != null && String(rid).trim() !== '' && (
                                                    <button
                                                        type="button"
                                                        className="add-listing-thumb-remove"
                                                        onClick={() => removeImageAPI(rid)}
                                                        disabled={
                                                            isDataLoading ||
                                                            removingImageId === String(rid)
                                                        }
                                                        aria-label="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {listingImages.map((item, idx) => (
                                        <div
                                            key={item.url}
                                            className="add-listing-thumb-wrap edit-listing-thumb-wrap--draggable"
                                            draggable
                                            title="Drag to reorder (saved when you Save changes)"
                                            onDragStart={(e) => handleReorderDragStart(e, 'new', idx)}
                                            onDragOver={handleReorderDragOver}
                                            onDrop={(e) => handleReorderDropNew(e, idx)}
                                        >
                                            <img src={item.url} alt="" className="add-listing-thumb" draggable={false} />
                                            <button
                                                type="button"
                                                className="add-listing-thumb-remove"
                                                onClick={() => removeListingImage(item.url)}
                                                disabled={isDataLoading}
                                                aria-label="Remove new image"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <h4 className="add-listing-section-title">Listing Info</h4>
                        <div className="detail-content">
                            {listingFieldIds.length > 0 ? renderDynamicFields() : <p>No fields to display.</p>}
                        </div>
                    </div>

                    <div className="btn-sec flex justify-center gap-4">
                        <button type="button" className="cancel-btn custom-btn" onClick={handleEditListingBack}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="save-btn custom-btn"
                            onClick={() => updateListingApi()}
                            disabled={isDataLoading}
                        >
                            Save changes
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
